import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowCounterClockwise, Blueprint, MagnifyingGlass, Plus, Trash,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DataTable } from '@/components/ui/DataTable';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { EmptyState } from '@/components/enterprise/EmptyState';
import { StatusBadge } from '@/components/enterprise/StatusBadge';
import { DevDeleteDialog } from './DevDeleteDialog';
import { GuideHelpButton } from './GuideHelpButton';
import { devApi, STAGE_TONE } from './devApi';
import { useT } from '@/i18n/LanguageContext';

export default function DevList() {
  const nav = useNavigate();
  const { t } = useT();
  const [params] = useSearchParams();
  const ctx = useOutletContext() || {};
  const isAdmin = !!ctx.access?.is_admin;
  const canEdit = isAdmin || !!ctx.access?.is_member;
  const [meta, setMeta] = useState({ stages: [], development_types: [] });
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(params.get('q') || '');
  const [stage, setStage] = useState(params.get('stage') || 'all');
  const [type, setType] = useState('all');
  const [showDeleted, setShowDeleted] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => { devApi.meta().then(setMeta).catch(() => {}); }, []);
  const load = useCallback(() => {
    // 削除済みの表示も同じ一覧APIの同じ経路（_live_filter の引数）で切り替える。
    devApi.list({
      q: q || undefined, stage, development_type: type,
      include_deleted: showDeleted && isAdmin ? true : undefined,
    }).then(setRows).catch(() => setRows([]));
  }, [q, stage, type, showDeleted, isAdmin]);
  useEffect(() => { load(); }, [load]);

  const restore = async (r) => {
    try {
      await devApi.restore(r.id);
      toast.success('開発案件を復元しました');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || '復元できませんでした'); }
  };

  const columns = [
    {
      key: 'title', header: '案件名', primary: true,
      cell: (r) => (
        <div className="min-w-0">
          <p className="flex items-center gap-2 truncate font-semibold text-zinc-900">
            <span className={r.is_deleted ? 'text-zinc-500 line-through' : ''}>{r.title}</span>
            {r.is_deleted && (
              <StatusBadge tone="danger" testid={`dev-list-deleted-${r.id}`}>削除済み</StatusBadge>
            )}
            {r.is_overdue && (
              <StatusBadge tone="danger" testid={`dev-list-overdue-${r.id}`}>
                {t('dev.badge.overdueDays', { n: r.overdue_days })}
              </StatusBadge>
            )}
            {r.needs_parts_setup && (
              <StatusBadge tone="warning" testid={`dev-list-parts-warning-${r.id}`}>
                {t('dev.badge.needsPartsSetup')}
              </StatusBadge>
            )}
          </p>
          <p className="truncate text-xs text-zinc-600">{r.project_name || 'プロジェクト未設定'}</p>
          {r.needs_parts_setup && (
            <p className="truncate text-xs text-amber-800"
              data-testid={`dev-list-parts-warning-detail-${r.id}`}>
              {(r.parts_gate_problems || []).join('、')}
            </p>
          )}
        </div>
      ),
    },
    { key: 'development_type_label', header: '種類', cell: (r) => r.development_type_label, sortable: true },
    {
      key: 'current_stage', header: 'ステージ', mobileBadge: true, sortable: true,
      cell: (r) => (
        <StatusBadge tone={STAGE_TONE[r.current_stage] || 'neutral'} testid={`dev-list-stage-${r.id}`}>
          {r.current_stage_label}
        </StatusBadge>
      ),
    },
    { key: 'owner', header: '担当者', cell: (r) => (r.owner_names || []).join('、') || '—' },
    {
      key: 'target_completion_date', header: '目標完了日', sortable: true,
      cell: (r) => (r.target_completion_date
        ? (
          <span className={r.is_overdue ? 'font-semibold text-rose-700' : ''}
            data-testid={`dev-list-target-${r.id}`}>{r.target_completion_date}</span>
        )
        : '—'),
    },
    canEdit && {
      key: 'actions', header: '', actions: true, width: 'w-28',
      cell: (r) => (r.is_deleted ? (
        <Button variant="outline" size="sm" className="h-7 px-2 text-xs"
          onClick={() => restore(r)} data-testid={`dev-list-restore-${r.id}`}>
          <ArrowCounterClockwise size={13} className="mr-1" /> 復元
        </Button>
      ) : (
        <Button variant="outline" size="sm"
          className="h-7 px-2 text-xs text-rose-700 hover:bg-rose-50 hover:text-rose-800"
          onClick={() => setToDelete(r)} data-testid={`dev-list-delete-${r.id}`}>
          <Trash size={13} className="mr-1" /> 削除
        </Button>
      )),
    },
  ];

  return (
    <div className="space-y-5" data-testid="dev-list-page">
      <PageHeader title={
        <span className="flex items-center gap-2">
          開発案件 <GuideHelpButton testid="dev-list-help-btn" />
        </span>
      } description="構想から完了・登録までの開発ライフサイクルを管理します。"
        testid="dev-list-header"
        primary={
          <Button onClick={() => nav('/engineering/developments/new')} data-testid="dev-list-new-btn">
            <Plus size={15} className="mr-1" /> 新しい開発案件
          </Button>
        } />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-2.5 top-2.5 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="案件名・プロジェクトで検索"
            className="w-64 pl-8" data-testid="dev-list-search-input" />
        </div>
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="w-40" data-testid="dev-list-stage-filter">
            <SelectValue placeholder="ステージ" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">すべてのステージ</SelectItem>
            {meta.stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-48" data-testid="dev-list-type-filter">
            <SelectValue placeholder="開発の種類" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">すべての種類</SelectItem>
            {meta.development_types.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {isAdmin && (
          <label className="flex items-center gap-2 text-sm text-zinc-800">
            <Checkbox checked={showDeleted} onCheckedChange={(v) => setShowDeleted(!!v)}
              data-testid="dev-list-show-deleted" />
            削除済みを表示
          </label>
        )}
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={Blueprint} title="該当する開発案件がありません"
          description="条件を変えて検索するか、新しい開発案件を登録してください。"
          testid="dev-list-empty" />
      ) : (
        <DataTable columns={columns} rows={rows} rowKey={(r) => r.id}
          onRowClick={(r) => nav(`/engineering/developments/${r.id}`)}
          rowTestId={(r) => `dev-list-row-${r.id}`} dataTestId="dev-list-table"
          defaultSort={{ key: 'target_completion_date', dir: 'asc' }} />
      )}

      <DevDeleteDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}
        dev={toDelete} onDeleted={load} />
    </div>
  );
}
